import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useJobPositionsService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { getEmployerJPState, useKv } from '@restworld/utils-common';
import { JPOnboardingType, toJobPositionPayload } from './types';
import { getValidationSchemaForStep } from './validation-schemas';
import {
  isOrgOnboardingModeAtom,
  orgOnboardingJPStepAtom,
  orgOnboardingStepAtom,
} from '../organization-onboarding/use-organization-onboarding';

export type JPOnboardingSteps =
  | 'venue_selection'
  | 'venue_onboarding'
  | 'role'
  | 'skills'
  | 'other_requirements'
  | 'contract_and_shifts'
  | 'salary'
  | 'benefits'
  | 'additional_notes'
  | 'ai_autogeneration'
  | 'review'
  | 'company_and_payment'
  | 'success';

// Sub-view types for the unified company_and_payment step
export type CompanyAndPaymentSubView =
  | 'loading'
  | 'product_card'
  | 'choose_company_type'
  | 'select_company'
  | 'insert_company'
  | 'add_payment'
  | 'show_payments'
  | 'confirm';

// Define the ordered list of steps for navigation (main flow, ends at review)
export const ORDERED_STEPS: JPOnboardingSteps[] = [
  'venue_selection',
  'role',
  'skills',
  'other_requirements',
  'contract_and_shifts',
  'salary',
  'benefits',
  'additional_notes',
  'ai_autogeneration',
  'review',
];

// Steps that should be skipped for non-PPU users (but shown for new org onboarding users)
export const PPU_ONLY_STEPS: JPOnboardingSteps[] = ['additional_notes'];

// Steps that can be accessed via explicit goToStep() calls only
export const SIDE_STEPS: JPOnboardingSteps[] = ['venue_onboarding'];

// Define which steps should NOT be counted in progress
export const UNCOUNTED_STEPS: JPOnboardingSteps[] = [
  'venue_selection',
  'venue_onboarding',
  'additional_notes',
  'ai_autogeneration',
  'review',
  'company_and_payment',
  'success',
];

// Get only the steps that should be counted
export const COUNTED_STEPS = ORDERED_STEPS.filter((step) => !UNCOUNTED_STEPS.includes(step));

// All valid steps (including side steps and post-review flow)
export const ALL_VALID_STEPS: JPOnboardingSteps[] = [...ORDERED_STEPS, ...SIDE_STEPS, 'company_and_payment', 'success'];

const stepResetState = 'venue_selection';
export const jpOnboardingStepsAtom = atom<JPOnboardingSteps>(stepResetState);

// // TODO: DELETE THIS HARDCODED JP ID AFTER DEV
// const jpResetValue = '345edad4-42bc-4f14-ad69-25dbfcda410e';
const jpResetValue = '';

// Atom to store the created JP ID
export const onboardingJobPositionIdAtom = atom<string>(jpResetValue);

// Atom set to `true` when the onboarding wizard is editing an already-online JobPosition.
// Drives the "edit-online" UX: skip payment/publish steps, rewrite the primary CTA to
// "save changes", warn that candidates in shortlist will be notified.
export const isEditingOnlineJpAtom = atom<boolean>(false);

// Atom holding the buffered formData for a not-yet-validated edit-online session,
// or `null` when the user has nothing pending. Hydrated from localStorage on wizard
// mount, written on every form change in edit-online mode, cleared after a successful
// commit or on explicit discard.
export const editOnlineBufferAtom = atom(null as Partial<JPOnboardingType> | null);

// Atom that surfaces a "resume unsaved changes" prompt right after wizard mount,
// when localStorage holds a buffer for the current JP and the form would otherwise
// reset to the server state.
export const resumeEditOnlinePromptOpenAtom = atom<boolean>(false);

// localStorage key prefix for the edit-online buffer, scoped per-JP so two JPs being
// edited in different tabs don't collide.
export const EDIT_ONLINE_BUFFER_KEY_PREFIX = 'jp-edit-online-buffer';

export const editOnlineBufferKey = (jpId: string): string => `${EDIT_ONLINE_BUFFER_KEY_PREFIX}:${jpId}`;

export const readEditOnlineBuffer = (jpId: string): Partial<JPOnboardingType> | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(editOnlineBufferKey(jpId));
    if (!raw) return null;
    return JSON.parse(raw) as Partial<JPOnboardingType>;
  } catch {
    return null;
  }
};

export const writeEditOnlineBuffer = (jpId: string, data: Partial<JPOnboardingType>): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(editOnlineBufferKey(jpId), JSON.stringify(data));
  } catch {
    // localStorage unavailable / quota exceeded — non-fatal; the worst case is the
    // user loses their pending changes on reload, same as today's behaviour.
  }
};

export const clearEditOnlineBuffer = (jpId: string): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(editOnlineBufferKey(jpId));
  } catch {
    // ignore
  }
};

// Atom to track AI autogeneration error state
export const aiAutoGenerationErrorAtom = atom<boolean>(false);

// Atom to show the preview modal of the JP
export const jobPreviewModalOpenAtom = atom<boolean>(false);

// Atom to track which job positions have had their preview modal auto-opened
// Maps job ID to boolean (true = preview has been shown for this job)
export const jobPreviewSeenByJobIdAtom = atom<Record<string, boolean>>({});

// LocalStorage key used to persist the preview-seen map across page refreshes
export const JOB_PREVIEW_SEEN_STORAGE_KEY = 'jp-onboarding-preview-seen';

// Type for dependent step information
export interface DependentStepInfo {
  step: JPOnboardingSteps;
  fields: string[];
}

// Atom to store dependent steps that need review after field changes
export const dependentStepsAtom = atom<DependentStepInfo[]>([]);

// Type for updated payment method information
export interface UpdatedPaymentMethodInfo {
  customerId: string;
  paymentMethodId: string;
}

// Atom to track if the default payment method was changed (stores customer ID and payment method ID)
export const updatedDefaultPaymentMethodAtom = atom(null as UpdatedPaymentMethodInfo | null);

// Atom to track the current sub-view within the company_and_payment step
export const companyAndPaymentSubViewAtom = atom<CompanyAndPaymentSubView>('loading');

// Atom to store the payment readiness status from the backend
export type PaymentReadinessStatus = 'ready' | 'missing_company' | 'missing_payment_method' | 'unpaid_invoices' | null;
export const paymentReadinessStatusAtom = atom(null as PaymentReadinessStatus);

// Org's total PPU credit balance across all sources (bulk_sales packages,
// referral bonuses, manual allowances), as returned by GET /payment_readiness.
// Drives the "Hai N crediti disponibili" banner on the company-and-payment
// step so the employer always sees their balance regardless of credit type.
export const availableCreditsAtom = atom<number>(0);

// Subset of `availableCreditsAtom` granted via `bulk_sales`. Only this subset
// unlocks the no-PM publish path: when > 0 the PM section is hidden on the
// confirm view and the employer can go online without a card on file. Other
// credit sources still consume against usage but the FE keeps the PM section
// visible so the employer can add one — we charge the card once those credits
// run out (no auto-offline).
export const availableBulkSalesCreditsAtom = atom<number>(0);

// Try for Free (deferred billing): true when GET /payment_readiness says the org
// may publish now with no company and no payment method. The confirm view then hides the
// billing card and explains that billing data is asked before the free days end.
export const billingDeferredAtom = atom<boolean>(false);

// The free days of the deferred billing campaign (GET /payment_readiness
// `deferred_billing_days_left`): the free days still unused, named by the confirm view's copy
// and its banner. Null outside deferred billing.
export const deferredBillingDaysLeftAtom = atom(null as number | null);

// 'deferred_billing' from GET /payment_readiness for an org that signed up with the Try for
// Free code, null otherwise. Sent with every event of the step to split the funnel.
export const billingFlowVariantAtom = atom(null as string | null);

// KV key prefix for storing JP onboarding step
const JP_ONBOARDING_KV_PREFIX = 'jp-onboarding-step';

/**
 * Hook to initialize JP Onboarding from URL params and KV store
 * Should be called once when the onboarding form is first mounted
 */
export const useJPOnboardingInitialization = (resetForm: (data: Partial<JPOnboardingType>) => void) => {
  const router = useRouter();
  const { getKV } = useKv();
  const jobPositionsService = useJobPositionsService();
  const setCurrentStep = useSetAtom(jpOnboardingStepsAtom);
  const setJpId = useSetAtom(onboardingJobPositionIdAtom);
  const setIsEditingOnline = useSetAtom(isEditingOnlineJpAtom);
  const setEditOnlineBuffer = useSetAtom(editOnlineBufferAtom);
  const setResumeEditOnlinePromptOpen = useSetAtom(resumeEditOnlinePromptOpenAtom);
  const { goToStep } = useJPOnboardingSteps();
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const initRef = useRef(false);

  // Reset onboarding to initial state.
  // Also clears `isEditingOnline`: that atom is a module-level Jotai singleton,
  // so without an explicit reset it survives across wizard mounts and contaminates
  // a fresh-create flow (footer shows "Salva modifiche", submitJP no-ops, the new
  // JP is never persisted).
  const resetOnboardingState = useCallback(() => {
    setJpId(jpResetValue);
    goToStep(stepResetState);
    setIsEditingOnline(false);
  }, [setJpId, goToStep, setIsEditingOnline]);

  useEffect(() => {
    // Prevent multiple initializations
    if (initRef.current || isInitialized) return;
    initRef.current = true;

    const initializeFromUrlAndKV = async () => {
      const { job_position_id, step } = router.query as {
        job_position_id?: string;
        step?: JPOnboardingSteps;
      };

      // Mark the wizard as "editing an online JP" so the footer swaps the primary CTA
      // from "Pubblica" to "Salva modifiche" and skips payment/publish steps.
      const syncEditingOnline = (jpData: { status_key?: string }) => {
        setIsEditingOnline(getEmployerJPState(jpData.status_key) === 'open');
      };

      // Resume-unsaved-changes prompt: when re-entering the wizard for a JP whose
      // edit-online session was abandoned (page reload, browser close), localStorage
      // still holds the buffered form values. We surface a modal asking the employer
      // whether to resume; the form initially renders the server state, so refusing
      // = discarding the buffer is a no-op on the visible state.
      const maybeOfferResume = (jpData: { status_key?: string }, jp_id: string) => {
        if (getEmployerJPState(jpData.status_key) !== 'open') {
          // Drafts have their own auto-save semantics elsewhere; the resume prompt
          // is only meaningful for online JPs where edits are buffered locally.
          return;
        }
        const buffered = readEditOnlineBuffer(jp_id);
        if (buffered) {
          setEditOnlineBuffer(buffered);
          setResumeEditOnlinePromptOpen(true);
        }
      };

      // Case 1: Both job_position_id and step exist in URL
      if (job_position_id && step && ALL_VALID_STEPS.includes(step)) {
        try {
          const response = await jobPositionsService.showJobPosition(job_position_id);
          if (response.data) {
            syncEditingOnline(response.data);
            // Set the JP ID and step from URL
            setJpId(job_position_id);
            setCurrentStep(step);
            // Reset form with fetched data — server state is the canonical baseline;
            // the resume prompt below decides whether to layer the localStorage buffer
            // on top of it (user opted to resume) or wipe it (user opted to discard).
            resetForm(response.data);
            maybeOfferResume(response.data, job_position_id);
            setIsInitialized(true);
            return;
          }
        } catch (error) {
          // If fetch fails (404 or other error), start fresh
          // eslint-disable-next-line no-console
          console.error('Error fetching job position from URL params:', error);
          resetOnboardingState();
          setIsInitialized(true);
          return;
        }
      }

      // Case 2: Only job_position_id exists in URL
      if (job_position_id && !step) {
        try {
          const response = await jobPositionsService.showJobPosition(job_position_id);
          if (response.data) {
            syncEditingOnline(response.data);
            // Set the JP ID from URL
            setJpId(job_position_id);
            // Reset form with fetched data
            resetForm(response.data);
            maybeOfferResume(response.data, job_position_id);

            // Try to get step from KV store
            const kvKey = `${JP_ONBOARDING_KV_PREFIX}-${job_position_id}`;
            const kvResult = await getKV(kvKey);

            if (kvResult?.value && ALL_VALID_STEPS.includes(kvResult.value as JPOnboardingSteps)) {
              // Navigate to the step from KV store
              setCurrentStep(kvResult.value as JPOnboardingSteps);
              // Update URL with the step
              router.replace(
                {
                  pathname: router.pathname,
                  query: { job_position_id, step: kvResult.value },
                },
                undefined,
                { shallow: true }
              );
            } else {
              // No step in KV, start from venue_selection with the loaded data
              setCurrentStep(stepResetState);
              router.replace(
                {
                  pathname: router.pathname,
                  query: { job_position_id, step: stepResetState },
                },
                undefined,
                { shallow: true }
              );
            }
            setIsInitialized(true);
            return;
          }
        } catch (error) {
          // If fetch fails, start fresh
          // eslint-disable-next-line no-console
          console.error('Error fetching job position:', error);
          resetOnboardingState();
          setIsInitialized(true);
          return;
        }
      }

      // Case 3: No URL params - start fresh
      // Reset to initial state (this handles clean start)
      resetOnboardingState();
      setIsInitialized(true);
    };

    // Only run when router is ready
    if (router.isReady) {
      initializeFromUrlAndKV();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    router.isReady,
    router.query,
    jobPositionsService,
    setJpId,
    setCurrentStep,
    resetForm,
    getKV,
    isInitialized,
    setIsInitialized,
    resetOnboardingState,
  ]);

  // Sync atom state with URL when browser back/forward navigation occurs
  // This pattern is based on libs/utils-common/src/hooks/wizard/wizard.hooks.tsx
  // When URL query changes (e.g., via browser back/forward), sync the atom state
  useEffect(() => {
    if (!router.isReady || !isInitialized) return;

    const { step: stepFromUrl, job_position_id: jpIdFromUrl } = router.query as {
      step?: JPOnboardingSteps;
      job_position_id?: string;
    };

    // Sync step if URL has a valid step, otherwise reset to initial state
    if (stepFromUrl && ALL_VALID_STEPS.includes(stepFromUrl)) {
      setCurrentStep(stepFromUrl);
    }

    // Sync JP ID if URL has a different value
    if (jpIdFromUrl) {
      setJpId(jpIdFromUrl);
    }
  }, [router.isReady, router.query, isInitialized, setCurrentStep, setJpId]);

  return { isInitialized };
};

/**
 * Hook for managing JP Onboarding form step state and progress
 * Provides utility functions for step transitions and progress tracking
 */
export const useJPOnboardingSteps = () => {
  const { t } = useTranslation('jp-onboarding');
  const router = useRouter();
  const { setKV } = useKv();
  const currentStep = useAtomValue(jpOnboardingStepsAtom);
  const aiAutoGenerationHasError = useAtomValue(aiAutoGenerationErrorAtom);
  const setCurrentStep = useSetAtom(jpOnboardingStepsAtom);
  const jpId = useAtomValue(onboardingJobPositionIdAtom);
  const { user } = useAuth();
  const isPPU = user?.using_ppu_business_model === true;
  const isOrgOnboarding = useAtomValue(isOrgOnboardingModeAtom);

  const currentStepIndex = ORDERED_STEPS.indexOf(currentStep);

  // Check if a step should be skipped based on user type
  // PPU_ONLY_STEPS are shown for PPU users AND organization onboarding users
  const shouldSkipStep = useCallback(
    (step: JPOnboardingSteps) => !isPPU && !isOrgOnboarding && PPU_ONLY_STEPS.includes(step),
    [isPPU, isOrgOnboarding]
  );

  // PPU users and org onboarding users see PPU-only steps in the progress bar; non-PPU users don't
  const effectiveUncountedSteps = useMemo(
    () => (isPPU || isOrgOnboarding ? UNCOUNTED_STEPS.filter((s) => !PPU_ONLY_STEPS.includes(s)) : UNCOUNTED_STEPS),
    [isPPU, isOrgOnboarding]
  );

  // Total steps that should be counted (PPU-only steps count for PPU users and org onboarding users)
  const totalSteps = useMemo(
    () => ORDERED_STEPS.filter((s) => !effectiveUncountedSteps.includes(s)).length,
    [effectiveUncountedSteps]
  );

  // Helper function to update URL with shallow routing
  const updateUrlWithState = useCallback(
    (step: JPOnboardingSteps, overrideJpId?: string) => {
      const effectiveJpId = overrideJpId ?? jpId;
      const query: { step: string; job_position_id?: string } = { step };
      if (effectiveJpId) {
        query.job_position_id = effectiveJpId;
      }
      router.push(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true }
      );
    },
    [router, jpId]
  );

  // Helper function to store step in KV store
  const storeStepInKV = useCallback(
    async (step: JPOnboardingSteps, overrideJpId?: string) => {
      const effectiveJpId = overrideJpId ?? jpId;
      if (!effectiveJpId) return;
      const kvKey = `${JP_ONBOARDING_KV_PREFIX}-${effectiveJpId}`;
      try {
        await setKV({ key: kvKey, value: step, type: 'jp_onboarding_step' });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error storing step in KV:', error);
      }
    },
    [setKV, jpId]
  );

  // Check if we're in org onboarding mode
  const isOrgOnboardingMode = useAtomValue(isOrgOnboardingModeAtom);
  const setOrgJPStep = useSetAtom(orgOnboardingJPStepAtom);
  // The current ORG-level step (job_position_creation in org onboarding). The
  // URL sync carries THIS value as ?step so Effect 2 of useOrgOnboardingUrlSync
  // reconciles to the correct org step.
  const orgCurrentStep = useAtomValue(orgOnboardingStepAtom);

  // Apply side effects: update URL and KV store
  // overrideJpId can be passed when the jpId atom hasn't been updated yet (e.g., after JP creation)
  // In org onboarding mode, also persist JP step to localStorage atom for refresh recovery.
  // URL is updated in org format: ?step=<org_step>&sub_step=<jp_step> where
  // <org_step> is the current ORG-level label (job_position_creation).
  // storeStepInKV is a no-op in org onboarding (no jpId → early return inside the function).
  const applySideEffects = useCallback(
    (step: JPOnboardingSteps, overrideJpId?: string) => {
      if (isOrgOnboardingMode) {
        // Persist JP sub-step so we can restore on refresh
        setOrgJPStep(step);
        // Update URL with org onboarding format (skip if already matches to avoid duplicate history entries)
        const currentQuery = router.query as Record<string, string | undefined>;
        if (currentQuery.step !== orgCurrentStep || currentQuery.sub_step !== step) {
          router.push({ pathname: router.pathname, query: { step: orgCurrentStep, sub_step: step } }, undefined, {
            shallow: true,
          });
        }
        return;
      }
      updateUrlWithState(step, overrideJpId);
      storeStepInKV(step, overrideJpId);
    },
    [updateUrlWithState, storeStepInKV, isOrgOnboardingMode, setOrgJPStep, router, orgCurrentStep]
  );

  // Navigate to next step with side effects (main flow only)
  // Skips PPU-only steps for non-PPU users
  const goToNextStep = useCallback(() => {
    let nextIndex = currentStepIndex + 1;
    while (nextIndex < ORDERED_STEPS.length && shouldSkipStep(ORDERED_STEPS[nextIndex])) {
      nextIndex++;
    }
    if (nextIndex < ORDERED_STEPS.length) {
      const nextStep = ORDERED_STEPS[nextIndex];
      setCurrentStep(nextStep);
      applySideEffects(nextStep);
    }
  }, [currentStepIndex, setCurrentStep, applySideEffects, shouldSkipStep]);

  // Navigate to previous step with side effects
  // Skips PPU-only steps for non-PPU users
  const goToPreviousStep = useCallback(() => {
    // From company_and_payment, go back to review
    if (currentStep === 'company_and_payment') {
      setCurrentStep('review');
      applySideEffects('review');
      return;
    }

    let prevIndex = currentStepIndex - 1;
    while (prevIndex >= 0 && shouldSkipStep(ORDERED_STEPS[prevIndex])) {
      prevIndex--;
    }
    if (prevIndex >= 0) {
      const prevStep = ORDERED_STEPS[prevIndex];
      setCurrentStep(prevStep);
      applySideEffects(prevStep);
    } else {
      setCurrentStep(stepResetState);
      applySideEffects(stepResetState);
    }
  }, [currentStep, currentStepIndex, setCurrentStep, applySideEffects, shouldSkipStep]);

  // Navigate to a specific step with side effects
  const goToStep = useCallback(
    (step: JPOnboardingSteps) => {
      setCurrentStep(step);
      // Updates URL and KV store
      applySideEffects(step);
    },
    [setCurrentStep, applySideEffects]
  );

  // Get progress information
  const getProgressInfo = useCallback(() => {
    // Check if current step should be counted (using effective set based on user type)
    const isUncountedStep = effectiveUncountedSteps.includes(currentStep);

    // Calculate the position within counted steps
    const countedStepsBeforeCurrent = ORDERED_STEPS.slice(0, currentStepIndex).filter(
      (step) => !effectiveUncountedSteps.includes(step)
    ).length;

    // For uncounted steps, show 0; for counted steps, show their position (1-indexed)
    const adjustedStepIndex = isUncountedStep ? 0 : countedStepsBeforeCurrent + 1;

    // Calculate percentage based on counted steps only
    const percentageCompleted = isUncountedStep ? 0 : Math.round((adjustedStepIndex / totalSteps) * 100);

    return {
      currentStep,
      currentStepIndex: adjustedStepIndex,
      totalSteps,
      percentageCompleted,
    };
  }, [currentStep, currentStepIndex, totalSteps, effectiveUncountedSteps]);
  // Get validation schema for current step
  const getCurrentStepValidationSchema = useCallback(() => {
    return getValidationSchemaForStep(currentStep, t);
  }, [currentStep, t]);

  // Determine if header should be shown
  const shouldShowHeader = useMemo(() => {
    // Hide header during AI autogeneration loading (only show if there's an error)
    if (currentStep === 'ai_autogeneration') {
      return aiAutoGenerationHasError;
    }
    // Hide header on success step (clean full-screen experience)
    if (currentStep === 'success') {
      return false;
    }
    return true;
  }, [currentStep, aiAutoGenerationHasError]);

  // Determine if footer should be shown
  const shouldShowFooter = useMemo(() => {
    // Hide footer during AI autogeneration loading (only show if there's an error)
    if (currentStep === 'ai_autogeneration') {
      return aiAutoGenerationHasError;
    }
    // Hide footer during venue onboarding (has its own footer)
    if (currentStep === 'venue_onboarding') {
      return false;
    }
    // Hide footer on success step (has its own CTA button)
    if (currentStep === 'success') {
      return false;
    }
    return true;
  }, [currentStep, aiAutoGenerationHasError]);

  return {
    currentStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    getProgressInfo,
    getCurrentStepValidationSchema,
    shouldShowHeader,
    shouldShowFooter,
    effectiveUncountedSteps,
    isFirstStep: currentStep === ORDERED_STEPS[0],
    isLastStep: currentStep === ORDERED_STEPS[ORDERED_STEPS.length - 1],
    applySideEffects,
  };
};

/**
 * Hook to handle JP form submission (create/update)
 * This hook manages the API calls for creating and updating the job position
 */
export const useJPOnboardingSubmit = () => {
  const currentStep = useAtomValue(jpOnboardingStepsAtom);
  const setCurrentStep = useSetAtom(jpOnboardingStepsAtom);
  const [jpId, setJpId] = useAtom(onboardingJobPositionIdAtom);
  const jobPositionsService = useJobPositionsService();
  const { applySideEffects } = useJPOnboardingSteps();
  const isOrgOnboardingMode = useAtomValue(isOrgOnboardingModeAtom);
  const isEditingOnline = useAtomValue(isEditingOnlineJpAtom);

  // Create JP using the service
  const createJP = useCallback(
    async (data: Partial<JPOnboardingType>) => {
      const response = await jobPositionsService.createJobPosition({ params: toJobPositionPayload(data) });
      const newJpId = response.data.id;
      setJpId(newJpId);
      // Update URL and KV with the new JP ID and next step
      const nextStepIndex = ORDERED_STEPS.indexOf(currentStep) + 1;
      const nextStep = ORDERED_STEPS[nextStepIndex] || currentStep;
      // Update the step atom
      setCurrentStep(nextStep);
      // Apply side effects with the new JP ID (override since atom hasn't updated yet)
      applySideEffects(nextStep, newJpId);
      return newJpId;
    },
    [jobPositionsService, setJpId, currentStep, setCurrentStep, applySideEffects]
  );

  // Update JP using the service
  const updateJP = useCallback(
    async (data: Partial<JPOnboardingType>) => {
      if (!jpId) {
        throw new Error('Cannot update JP: JP ID is not defined');
      }

      await jobPositionsService.updateJobPosition({
        jobPositionId: jpId,
        params: toJobPositionPayload(data),
      });
    },
    [jobPositionsService, jpId]
  );

  // Determine if we should create or update
  // In org onboarding mode, never create (data stored locally until bulk submission)
  const shouldCreateJP = useMemo(
    () => !isOrgOnboardingMode && currentStep === 'role' && (!jpId || jpId === ''),
    [currentStep, jpId, isOrgOnboardingMode]
  );

  // Request to publish JP using the service
  // In org onboarding mode, this is a no-op (publication happens after entity creation)

  const requestToPublishJP = useCallback(async () => {
    if (isOrgOnboardingMode) return; // Skip in org onboarding mode
    if (!jpId) {
      throw new Error('Cannot request to publish JP: JP ID is not defined');
    }

    await jobPositionsService.requestToPublishJobPosition({
      jobPositionId: jpId,
    });
  }, [jobPositionsService, jpId, isOrgOnboardingMode]);

  /**
   * Validate ad content via AI.
   *
   * Prefers jpId when available (works in both standard and org onboarding modes).
   * Falls back to raw jpData only when no jpId exists (initial org onboarding before entity creation).
   */
  const validateAdJP = useCallback(
    async (jpData?: Record<string, unknown>, rateLimitKey?: string) => {
      // Prefer ID-based validation when jpId is available
      if (jpId) {
        const response = await jobPositionsService.validateAd({ jobPositionId: jpId });
        return response.data;
      }

      // Fallback: validate from raw data (org onboarding without jpId)
      if (jpData) {
        const response = await jobPositionsService.validateAd({ jpData, rateLimitKey });
        return response.data;
      }

      throw new Error('Cannot validate ad: JP ID is not defined and no jpData provided');
    },
    [jobPositionsService, jpId]
  );

  // Put JP online (PPU flow - company_and_payment confirm step)
  const goOnlineJP = useCallback(async () => {
    if (!jpId) {
      throw new Error('Cannot go online: JP ID is not defined');
    }

    await jobPositionsService.goOnline({ jobPositionId: jpId });
  }, [jobPositionsService, jpId]);

  // Check payment readiness (PPU flow - company_and_payment step)
  const checkPaymentReadinessJP = useCallback(async () => {
    const response = await jobPositionsService.checkPaymentReadiness();
    return response.data;
  }, [jobPositionsService]);

  // Submit handler - decides whether to create or update
  // In org onboarding mode, this is a no-op (data persistence is handled by applySideEffects).
  // In edit-online mode, this is also a no-op: edits live in the form / localStorage until
  // the employer explicitly clicks "Validate & save" — only at that point we hit the DB,
  // and only if the AI validation passes. Persisting on step-advance would push partial
  // unvalidated changes online, breaking the contract that an online edit == publish.
  const submitJP = useCallback(
    async (data: Partial<JPOnboardingType>) => {
      if (isEditingOnline) {
        return jpId || null;
      }
      if (!isOrgOnboardingMode && shouldCreateJP) {
        return await createJP(data);
      }
      if (jpId && jpId !== '') {
        await updateJP(data);
        return jpId;
      }
      return null;
    },
    [isEditingOnline, shouldCreateJP, createJP, updateJP, jpId, isOrgOnboardingMode]
  );

  /**
   * Edit-online commit: bypasses the `submitJP` gate above. Called by
   * `handleSaveOnlineEdits` only AFTER the AI ad validation has approved the
   * changes. This is the single point that turns local edits into a real DB
   * update for an already-online JP.
   *
   * Validate-against-data: pass `{ jpData: payload }` to `validateAd` so the AI
   * sees the candidate copy without needing it persisted first.
   */
  const validateAdData = useCallback(
    async (data: Partial<JPOnboardingType>, rateLimitKey?: string) => {
      const response = await jobPositionsService.validateAd({
        jpData: toJobPositionPayload(data),
        rateLimitKey,
      });
      return response.data;
    },
    [jobPositionsService]
  );

  const commitOnlineEdits = useCallback(
    async (data: Partial<JPOnboardingType>) => {
      if (!jpId) {
        throw new Error('Cannot commit online edits: JP ID is not defined');
      }
      await jobPositionsService.updateJobPosition({
        jobPositionId: jpId,
        params: toJobPositionPayload(data),
      });
    },
    [jobPositionsService, jpId]
  );

  return {
    submitJP,
    requestToPublishJP,
    validateAdJP,
    validateAdData,
    commitOnlineEdits,
    goOnlineJP,
    checkPaymentReadinessJP,
    shouldCreateJP,
  };
};

/**
 * Writes the live form state into localStorage whenever the form changes, but only
 * while the wizard is in edit-online mode and only after the form has been actually
 * touched by the user (RHF `isDirty`). Reading happens at wizard init in
 * `useJPOnboardingInitialization` via the resume prompt.
 *
 * Should be mounted once inside the wizard, scoped under the FormProvider so
 * `useFormContext` can read RHF state.
 */
export const useEditOnlineBufferWriter = (watchedValues: Partial<JPOnboardingType>, isDirty: boolean) => {
  const isEditingOnline = useAtomValue(isEditingOnlineJpAtom);
  const jpId = useAtomValue(onboardingJobPositionIdAtom);

  useEffect(() => {
    if (!isEditingOnline || !jpId) return;
    if (!isDirty) return;
    writeEditOnlineBuffer(jpId, watchedValues);
  }, [isEditingOnline, jpId, isDirty, watchedValues]);
};
